import React from 'react';

interface NativeMarkdownViewerProps {
  content: string;
  className?: string;
}

export const NativeMarkdownViewer: React.FC<NativeMarkdownViewerProps> = ({ content, className = '' }) => {
  if (!content) return null;

  // Split lines and parse basic structures safely without external markdown dependencies
  const lines = content.split('\n');

  return (
    <div className={`space-y-2 text-sm leading-relaxed ${className}`}>
      {lines.map((line, idx) => {
        const trimmed = line.trim();

        // Heading 3: ###
        if (trimmed.startsWith('### ')) {
          return (
            <h4 key={idx} className="text-base font-bold text-teal-400 mt-3 mb-1 border-b border-slate-800 pb-1">
              {trimmed.replace('### ', '')}
            </h4>
          );
        }

        // Heading 2: ##
        if (trimmed.startsWith('## ')) {
          return (
            <h3 key={idx} className="text-lg font-bold text-slate-100 mt-4 mb-2">
              {trimmed.replace('## ', '')}
            </h3>
          );
        }

        // Heading 1: #
        if (trimmed.startsWith('# ')) {
          return (
            <h2 key={idx} className="text-xl font-extrabold text-teal-300 mt-4 mb-2">
              {trimmed.replace('# ', '')}
            </h2>
          );
        }

        // Bullet point: - or *
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || trimmed.startsWith('• ')) {
          const itemText = trimmed.replace(/^[-*•]\s+/, '');
          return (
            <div key={idx} className="flex items-start gap-2 pl-2">
              <span className="text-teal-400 font-bold mt-1 text-xs">•</span>
              <span className="text-slate-300">{renderFormattedInline(itemText)}</span>
            </div>
          );
        }

        // Numbered list: 1. 2. etc
        if (/^\d+\.\s/.test(trimmed)) {
          const num = trimmed.match(/^(\d+\.)\s/)?.[1] || '';
          const rest = trimmed.replace(/^\d+\.\s/, '');
          return (
            <div key={idx} className="flex items-start gap-2 pl-2">
              <span className="text-teal-400 font-mono text-xs font-semibold pt-0.5">{num}</span>
              <span className="text-slate-300">{renderFormattedInline(rest)}</span>
            </div>
          );
        }

        // Empty line
        if (trimmed === '') {
          return <div key={idx} className="h-1.5" />;
        }

        // Standard paragraph
        return (
          <p key={idx} className="text-slate-300">
            {renderFormattedInline(line)}
          </p>
        );
      })}
    </div>
  );
};

// Formats **bold** and `code` inline elements safely
function renderFormattedInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*.*?\*\*|`.*?`)/g);

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="text-slate-100 font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={index} className="bg-slate-800 text-teal-300 px-1 py-0.5 rounded text-xs font-mono">
          {part.slice(1, -1)}
        </code>
      );
    }
    return part;
  });
}
